<template>
  <Loader
    :value="promises.fetch"
    :message="`${this.$t('loading', { resource: this.$tc('task', 2).toLowerCase() })}...`"
    class="task-list"
  >
    <DataTable
      sticky
      :columns="columns"
      :value="items"
      :row-wrapper="
        item => ({
          component: 'router-link',
          props: { to: { path: $route.path, query: { ...$route.query, task: item.id } }, style: 'color: inherit;' },
        })
      "
      v-bind="{
        ...$attrs,
        ...(select ? { selected: selectedComp } : {}),
      }"
      v-on="select ? { 'update:selected': $ev => (selectedComp = $ev) } : {}"
    >
      <template v-slot:[id]="{ item }" v-for="{ id, component, path, disabled } in columns">
        <component
          v-if="!disabled"
          :is="Types[component || id.toTitleCase()] || Types.Default"
          :value="$path(path || id, item)"
          :task="item"
          :key="id"
        />
      </template>

      <template #select-actions>
        <div v-if="hasAccess">
          <ButtonComponent :label="$t('delete')" :icon="['fal', 'trash']" @click="context = selected" />
        </div>
      </template>
    </DataTable>
    <EmptyState
      v-if="!items.length"
      :icon="['fal', 'tasks']"
      :title="$t('noTasks')"
      :subtitle="$t('filterEmptyState')"
    />
    <RemoveComponent
      :show.sync="removeDialog"
      :title="context.map(c => $path('title', c)).join('<br />')"
      :action="() => remove()"
      @onSuccess="reset()"
    />
  </Loader>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Debounce, Capitalize } from '@/utils'
import { Moment } from '@/filters'

import Types from './Types'
import EmptyState from '@/components/EmptyState'
import DataTable from '@/components/DataTable'
import RemoveComponent from '@/components/Task/Remove'
import Bus from '@/bus'

import tracker from '@/tracking'

const availableColumns = [
  'title',
  'status',
  'dueAt',
  'type',
  'reference',
  'project',
  'members',
  'updatedAt',
  'createdAt',
  'createdBy',
]

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({}),
    },
    columns: {
      type: Array,
      required: true,
    },
    select: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    columns: {
      handler() {
        this.fetch()
      },
      deep: true,
    },
    filter: {
      handler(newVal, oldVal) {
        this.fetch('filter')
      },
      immediate: true,
      deep: true,
    },
  },
  data() {
    return {
      Types,
      selected: [],
      context: [],
      preventTrackFirstFilter: false,
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapState('Task', {
      items: i =>
        i.items.map(item => {
          return {
            ...item,

            updatedAt: Moment(item.updatedAt),
            createdAt: Moment(item.createdAt),
          }
        }),
      promises: i => i.promises,
    }),
    hasAccess() {
      if (!this.user) return false
      return ['admin', 'complaints'].some(r => this.user.access.includes(r))
    },
    removeDialog: {
      get() {
        return Boolean(this.context && this.context.length)
      },
      set(val) {
        if (!val) this.context = []
      },
    },

    selectedComp: {
      get() {
        return this.hasAccess ? this.selected : undefined
      },
      set(val) {
        this.selected = val
      },
    },
  },
  methods: {
    ...mapActions('Task', {
      fetch: Debounce(function (dispatch, v) {
        const filter = this.transformFilter('project', this.filter)

        if (v) {
          if (!this.preventTrackFirstFilter) {
            this.preventTrackFirstFilter = true
          } else if (Object.values(this[v]).filter(v => (Array.isArray(v) ? v.length : Boolean(v))).length) {
            tracker.emit(`Task:${v}`, { ...this[v] })
          }
        }

        return dispatch('fetch', {
          filter,
          ...Object.fromEntries(
            availableColumns.map(id => {
              let column = this.columns.find(c => c.id === id)

              return [`skip${id.toTitleCase()}`, Boolean(column ? column.disabled : true)]
            }),
          ),
        })
      }, 200),
    }),
    transformFilter(context, item) {
      if (!item[context]) {
        delete item[context]
        return item
      }

      const result = {
        ...item,
        references: [{ onModel: Capitalize(context), ref: item[context], inherit: true }],
      }

      delete result[context]
      return result
    },
    remove() {
      if (!this.context) return
      return Promise.all(this.context.map(c => this.$store.dispatch('Task/remove', { id: c.id })))
    },
    reset() {
      this.selected = []
      this.fetch()
      Bus.$emit('data-table:reset')
    },
  },
  created() {
    const onTaskChange = id => {
      this.fetch()
    }

    this.$bus.$on('task:create', ({ id }) => onTaskChange(id))
    this.$bus.$on('task:update', ({ id }) => onTaskChange(id))

    this.$bus.$on('task:remove', id => onTaskChange(id))
    this.$bus.$on('tasks:fetch', () => onTaskChange())
  },
  components: {
    EmptyState,
    DataTable,
    RemoveComponent,
  },
}
</script>

<style lang="scss">
.task-list {
  &__filter {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  &__add {
    .elder-dropdown__trigger {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 0.5rem;

      .elder-button {
        height: 30px;
        aspect-ratio: 1/1;
        border-radius: 50%;

        &__icon {
          padding: 0 !important;
        }
      }
    }
  }
}
</style>
